import * as Three from 'three/webgpu';
import { z } from 'zod';

import { InstanceIdType } from '#core/schema/defs';
import { CogniteDescribableModel } from '#core/schema/model/cdm/CogniteDescribableModel';

import { WellModel } from '#schema/model/WellModel';

export const WellboreGeometryModel = z
  .object({
    ...CogniteDescribableModel.shape,

    md: z.number().array().describe('Measured depth array of the wellbore geometry.'),
    tvd: z.number().array().describe('True vertical depth array of the wellbore geometry.'),
    xdelta: z.number().array().describe('X delta array of the wellbore geometry.'),
    ydelta: z.number().array().describe('Y delta array of the wellbore geometry.'),
    wellstring: InstanceIdType.optional().describe('Reference to string data for this wellbore geometry.'),
    well: WellModel.or(InstanceIdType).optional().describe('Reference to well data for this wellbore geometry.'),
    completion: InstanceIdType.array()
      .optional()
      .describe('References to Completion assets for this wellbore geometry.'),
  })
  .describe('View for WellboreGeometry data. Contains wellbore geometry data for a well string.')
  .transform((props) => {
    const { md, tvd, xdelta, ydelta } = props;
    const points = Array.from({ length: md.length }, (_, i) => ({
      md: md[i],
      tvd: tvd[i],
      xdelta: xdelta[i],
      ydelta: ydelta[i],
      position: new Three.Vector3(xdelta[i] ?? 0, -(tvd[i] ?? 0), -(ydelta[i] ?? 0)),
    }));

    const bbox = new Three.Box3().setFromPoints(points.map((point) => point.position));
    const centroid = new Three.Vector3();
    bbox.getCenter(centroid);

    return { ...props, points, bbox, centroid };
  });

export type WellboreGeometryModelInput = z.input<typeof WellboreGeometryModel>;
export type WellboreGeometryModelOutput = z.output<typeof WellboreGeometryModel>;
